import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { request } from "../api/http";
import {
  abortQuestRun,
  cancelQuestRun,
  completeQuestRun,
  createQuestRun,
  getQuestRun,
  respondQuestRun,
  startQuestRun,
  type QuestRun,
} from "../api/endpoints";
import { loadQuests } from "../offline/catalogCache";
import { syncCatalogIfChangedWeb } from "../offline/catalogSyncWeb";
import QuestContentCard from "../components/QuestContentCard";

type QuestRow = {
  id?: string | number;
  slug?: string | null;
  title?: string | null;
  body?: string | null;
  excerpt?: string | null;
  summary?: string | null;
  warning?: string | null;
  meta?: any;
  is_adult?: boolean;
  isAdult?: boolean;
  adult?: boolean;
  kind?: string | null;
  category?: string | null;
  category_key?: string | null;
  estimatedMinutes?: number | null;
  durationMinutes?: number | null;
  minutes?: number | null;
  est_duration?: string | null;
  modes?: string[] | string | null;
  stories?: any[] | null;
};

type Mood = "DEFAULT" | "ROMANTIC" | "PLAYFUL" | "BOLD";

function isAdultQuest(q: any): boolean {
  return q?.is_adult === true || q?.isAdult === true || q?.adult === true || q?.kind === "adult";
}

function questMinutes(q: any): string {
  const direct = q?.estimatedMinutes ?? q?.durationMinutes ?? q?.minutes ?? null;
  if (direct) return `${direct} min`;
  const txt = String(q?.est_duration || "").trim();
  return txt || "";
}

function safeMeta(q: any) {
  return q?.meta && typeof q.meta === "object" && !Array.isArray(q.meta) ? q.meta : {};
}

function collectWarnings(q: any): string[] {
  const meta = safeMeta(q);
  const raw = meta?.warnings;
  if (Array.isArray(raw)) return raw.map((x: any) => String(x || "").trim()).filter(Boolean);
  if (typeof q?.warning === "string" && q.warning.trim()) return [q.warning.trim()];
  return [];
}

function moodLabel(m: Mood) {
  if (m === "ROMANTIC") return "Romantic";
  if (m === "PLAYFUL") return "Playful";
  if (m === "BOLD") return "Bold";
  return "Default";
}

function getAvailableMoods(q: any): Mood[] {
  const meta = safeMeta(q);
  const raw = Object.keys(meta?.styles || {}).map((x) => String(x || "").toUpperCase().trim());
  const out: Mood[] = ["DEFAULT"];
  if (raw.includes("ROMANTIC")) out.push("ROMANTIC");
  if (raw.includes("PLAYFUL")) out.push("PLAYFUL");
  if (raw.includes("BOLD")) out.push("BOLD");
  return out;
}

function getAvailableVariations(q: any) {
  const meta = safeMeta(q);
  const vars = Array.isArray(meta?.variations) ? meta.variations : [];
  return vars
    .map((v: any, idx: number) => {
      const key = String(v?.key || `variation-${idx}`).trim();
      const label = String(v?.label || key).trim();
      return key ? { key, label } : null;
    })
    .filter(Boolean) as Array<{ key: string; label: string }>;
}

function getHeroImage(q: any): string {
  const meta = safeMeta(q);
  return String(meta?.imageAsset?.uri || meta?.heroImage || "").trim();
}

function questModes(q: any): string[] {
  const raw = q?.modes;
  if (Array.isArray(raw)) {
    return raw.map((m: any) => String(m || "").toLowerCase().trim()).filter(Boolean);
  }
  if (typeof raw === "string" && raw.trim()) {
    return raw.split(",").map((m) => m.toLowerCase().trim()).filter(Boolean);
  }
  return [];
}

function isSoloOnlyQuest(q: any): boolean {
  const modes = questModes(q);
  return modes.includes("solo") && modes.every((m) => m === "solo");
}

function isPartnerQuest(q: any): boolean {
  const modes = questModes(q);
  return modes.some((m) => ["partner", "couple", "relationship", "shared"].includes(m));
}


function readPreferredLang() {
  if (typeof window === "undefined") return "en";
  const raw =
    window.localStorage.getItem("lang") ||
    window.localStorage.getItem("locale") ||
    "en";
  const v = String(raw || "en").trim().toLowerCase();
  return v || "en";
}

export default function QuestDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const runIdParam = String(searchParams.get("runId") || "").trim();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quest, setQuest] = useState<QuestRow | null>(null);
  const [language, setLanguage] = useState<string>(readPreferredLang());
  const [mood, setMood] = useState<Mood>("DEFAULT");
  const [variation, setVariation] = useState<string>("");
  const [run, setRun] = useState<QuestRun | null>(null);
  const [runLoading, setRunLoading] = useState(false);
  const [runBusy, setRunBusy] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const cached = await loadQuests().catch(() => null);
        const cachedItem =
          cached?.items?.find((x: any) => String(x?.id || "") === String(id)) ||
          cached?.items?.find((x: any) => String(x?.slug || "") === String(id)) ||
          null;

        if (alive && cachedItem) {
          setQuest(cachedItem);
        }

        try {
          await syncCatalogIfChangedWeb();
        } catch {}

        const refreshed = await loadQuests().catch(() => null);
        const refreshedItem =
          refreshed?.items?.find((x: any) => String(x?.id || "") === String(id)) ||
          refreshed?.items?.find((x: any) => String(x?.slug || "") === String(id)) ||
          null;

        if (alive && refreshedItem) {
          setQuest(refreshedItem);
          setError(null);
          setLoading(false);
          return;
        }

        const res = await request(`/quests/${encodeURIComponent(String(id))}`, { auth: true });
        const item = res?.item || res?.quest || res || null;
        if (!alive) return;
        if (!item || (!item.id && !item.slug)) {
          setQuest(cachedItem || null);
          setError(cachedItem ? null : "Quest not found.");
        } else {
          setQuest(item);
        }
      } catch (err: any) {
        if (!alive) return;
        const cached = await loadQuests().catch(() => null);
        const cachedItem =
          cached?.items?.find((x: any) => String(x?.id || "") === String(id)) ||
          cached?.items?.find((x: any) => String(x?.slug || "") === String(id)) ||
          null;
        setQuest(cachedItem || null);
        setError(cachedItem ? null : (err?.response?.data?.error || err?.message || "Could not load quest."));
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [id]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("lang", language);
    }
  }, [language]);

  useEffect(() => {
    setMood("DEFAULT");
    setVariation("");
  }, [id]);


  useEffect(() => {
    let alive = true;

    if (!runIdParam) {
      setRun(null);
      setRunError(null);
      return () => {
        alive = false;
      };
    }

    (async () => {
      setRunLoading(true);
      setRunError(null);
      try {
        const res = await getQuestRun(runIdParam);
        if (!alive) return;
        setRun(res?.item || res?.run || null);
      } catch (err: any) {
        if (!alive) return;
        setRun(null);
        setRunError(err?.data?.error || err?.message || "Could not load quest run.");
      } finally {
        if (alive) setRunLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [runIdParam]);


  const warnings = useMemo(() => collectWarnings(quest), [quest]);
  const hasStories = Array.isArray(quest?.stories) && quest.stories.length > 0;
  const soloOnly = useMemo(() => isSoloOnlyQuest(quest), [quest]);
  const partnerQuest = useMemo(() => isPartnerQuest(quest), [quest]);
  const meta = useMemo(() => safeMeta(quest), [quest]);
  const heroImage = useMemo(() => getHeroImage(quest), [quest]);
  const availableMoods = useMemo(() => getAvailableMoods(quest), [quest]);
  const availableVariations = useMemo(() => getAvailableVariations(quest), [quest]);

  const displayQuest = useMemo(() => {
    if (!quest) return null;
    const next = { ...quest, meta: { ...meta } };

    if (mood !== "DEFAULT") {
      const moodBody = meta?.styles?.[mood]?.body;
      if (typeof moodBody === "string" && moodBody.trim()) {
        next.body = moodBody;
      }
    }

    if (variation) {
      const hit = availableVariations.find((v) => v.key === variation);
      if (hit) {
        next.meta = {
          ...next.meta,
          variation_label: hit.label,
        };
      }
    }

    return next;
  }, [quest, meta, mood, variation, availableVariations]);


  async function handleCreateRun() {
    if (!quest) return;

    const questId = String(quest.id || quest.slug || id || "").trim();
    if (!questId) return;

    setRunBusy(true);
    setRunError(null);
    try {
      const payload: Record<string, any> = {};
      if (mood !== "DEFAULT") payload.style = mood;
      if (variation) payload.variation = variation;

      const res = await createQuestRun(questId, payload);
      const item = res?.item || res?.run || null;
      if (item) {
        setRun(item);
        const nextRunId = String(item.id || "").trim();
        if (nextRunId) setSearchParams({ runId: nextRunId });
      }
    } catch (err: any) {
      setRunError(err?.data?.error || err?.message || "Could not start quest.");
    } finally {
      setRunBusy(false);
    }
  }

  async function applyRunAction(action: "accept" | "decline" | "start" | "complete" | "abort" | "cancel") {
    const rid = String(run?.id || runIdParam || "").trim();
    if (!rid) return;

    setRunBusy(true);
    setRunError(null);
    try {
      const res =
        action === "accept"
          ? await respondQuestRun(rid, { decision: "accept", response: "accepted", status: "accepted" })
          : action === "decline"
          ? await respondQuestRun(rid, { decision: "decline", response: "declined", status: "declined" })
          : action === "start"
          ? await startQuestRun(rid)
          : action === "complete"
          ? await completeQuestRun(rid)
          : action === "abort"
          ? await abortQuestRun(rid)
          : await cancelQuestRun(rid);

      const item = res?.item || res?.run || null;
      if (item) setRun(item);
    } catch (err: any) {
      setRunError(err?.data?.error || err?.message || `Could not ${action} quest.`);
    } finally {
      setRunBusy(false);
    }
  }

  const runStatus = String(run?.status || "").toLowerCase();

  if (loading) {
    return <div className="center-page">Loading quest…</div>;
  }

  if (error || !quest || !displayQuest) {
    return (
      <div className="space-y-5">
        <div className="rounded-3xl border border-slate-800 bg-slate-950/70 p-5 text-sm text-slate-300">
          {error || "Quest not found."}
        </div>
        <div>
          <button
            type="button"
            onClick={() => navigate("/quests")}
            className="rounded-full bg-pink-500 px-4 py-2 text-sm font-semibold text-white hover:bg-pink-400"
          >
            Back to quests
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="rounded-3xl border border-slate-800 bg-slate-950/70 p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-pink-300">
              Quest
            </div>
            <h1 className="mt-2 text-2xl font-extrabold text-slate-50">
              {quest.title || "Untitled quest"}
            </h1>
            {!!(quest.summary || quest.excerpt) && (
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-300">
                {quest.summary || quest.excerpt}
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {!!questMinutes(quest) && (
              <span className="rounded-full border border-slate-700 bg-slate-900/70 px-3 py-1.5 text-xs font-semibold text-slate-300">
                {questMinutes(quest)}
              </span>
            )}
            <span className="rounded-full border border-slate-700 bg-slate-900/70 px-3 py-1.5 text-xs font-semibold text-slate-300">
              Guided
            </span>
          </div>
        </div>
      </header>

      {!!heroImage && (
        <section className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-950/70">
          <img
            src={heroImage}
            alt={quest.title || "Quest image"}
            className="h-[280px] w-full object-cover"
          />
        </section>
      )}

      <section className="rounded-3xl border border-slate-800 bg-slate-950/70 p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400">
              Briefing
            </div>
            <h2 className="mt-1 text-lg font-bold text-slate-50">
              Read first, then play
            </h2>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs text-slate-400">
              <span className="mr-2">Language</span>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="rounded-full border border-slate-700 bg-slate-900 px-3 py-1.5 text-xs text-slate-200"
              >
                <option value="en">EN</option>
                <option value="nl">NL</option>
                <option value="de">DE</option>
                <option value="fr">FR</option>
                <option value="zh">ZH</option>
              </select>
            </label>

            <button
              type="button"
              onClick={() => navigate("/quests")}
              className="rounded-full border border-slate-700 bg-slate-900/80 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:border-slate-500"
            >
              Back
            </button>
          </div>
        </div>

        {availableMoods.length > 1 && (
          <div className="flex flex-wrap gap-2">
            {availableMoods.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMood(m)}
                className={
                  "rounded-full px-3 py-1.5 text-xs font-semibold border " +
                  (mood === m
                    ? "border-pink-400/40 bg-pink-500/15 text-pink-200"
                    : "border-slate-700 bg-slate-900/70 text-slate-300")
                }
              >
                {moodLabel(m)}
              </button>
            ))}
          </div>
        )}

        {availableVariations.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setVariation("")}
              className={
                "rounded-full px-3 py-1.5 text-xs font-semibold border " +
                (!variation
                  ? "border-pink-400/40 bg-pink-500/15 text-pink-200"
                  : "border-slate-700 bg-slate-900/70 text-slate-300")
              }
            >
              Default
            </button>
            {availableVariations.map((v) => (
              <button
                key={v.key}
                type="button"
                onClick={() => setVariation(v.key)}
                className={
                  "rounded-full px-3 py-1.5 text-xs font-semibold border " +
                  (variation === v.key
                    ? "border-pink-400/40 bg-pink-500/15 text-pink-200"
                    : "border-slate-700 bg-slate-900/70 text-slate-300")
                }
              >
                {v.label}
              </button>
            ))}
          </div>
        )}

        <QuestContentCard quest={displayQuest} language={language} />
      </section>


      <section className="rounded-3xl border border-slate-800 bg-slate-950/70 p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400">
              Quest run
            </div>
            <h2 className="mt-1 text-lg font-bold text-slate-50">
              {run ? `Status: ${run.status || "active"}` : "Ready to start"}
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-300">
              {runLoading
                ? "Loading run state…"
                : run
                ? "Continue this quest run or mark it when you are done."
                : "Create a run to track progress and keep Home in sync."}
            </p>
            {!!runError && (
              <p className="mt-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">
                {runError}
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {!run && (
              <button
                type="button"
                onClick={handleCreateRun}
                disabled={runBusy || runLoading}
                className="rounded-full bg-pink-500 px-4 py-2 text-sm font-semibold text-white hover:bg-pink-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {runBusy ? "Starting…" : "Start quest"}
              </button>
            )}

            {!!run && runStatus === "accepted" && (
              <button
                type="button"
                onClick={() => applyRunAction("start")}
                disabled={runBusy || runLoading}
                className="rounded-full bg-pink-500 px-4 py-2 text-sm font-semibold text-white hover:bg-pink-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Start
              </button>
            )}

            {!!run && runStatus === "in_progress" && (
              <>
                <button
                  type="button"
                  onClick={() => applyRunAction("complete")}
                  disabled={runBusy || runLoading}
                  className="rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Complete
                </button>
                <button
                  type="button"
                  onClick={() => applyRunAction("abort")}
                  disabled={runBusy || runLoading}
                  className="rounded-full border border-slate-700 bg-slate-900/80 px-4 py-2 text-sm font-semibold text-slate-200 hover:border-slate-500 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Abort
                </button>
              </>
            )}

            {!!run && runStatus === "pending" && run.actionRequired && (
              <>
                <button
                  type="button"
                  onClick={() => applyRunAction("accept")}
                  disabled={runBusy || runLoading}
                  className="rounded-full bg-pink-500 px-4 py-2 text-sm font-semibold text-white hover:bg-pink-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Accept
                </button>
                <button
                  type="button"
                  onClick={() => applyRunAction("decline")}
                  disabled={runBusy || runLoading}
                  className="rounded-full border border-slate-700 bg-slate-900/80 px-4 py-2 text-sm font-semibold text-slate-200 hover:border-slate-500 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Decline
                </button>
              </>
            )}

            {!!run && runStatus === "pending" && !run.actionRequired && (
              <span className="rounded-full border border-slate-700 bg-slate-900/80 px-4 py-2 text-sm font-semibold text-slate-300">
                Waiting for partner
              </span>
            )}

            {!!run && ((runStatus === "pending" && !run.actionRequired) || runStatus === "accepted") && (
              <button
                type="button"
                onClick={() => applyRunAction("cancel")}
                disabled={runBusy || runLoading}
                className="rounded-full border border-slate-700 bg-slate-900/80 px-4 py-2 text-sm font-semibold text-slate-200 hover:border-slate-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
            )}

            {!!run && (runStatus === "completed" || runStatus === "aborted" || runStatus === "cancelled" || runStatus === "canceled") && (
              <button
                type="button"
                onClick={handleCreateRun}
                disabled={runBusy || runLoading}
                className="rounded-full bg-pink-500 px-4 py-2 text-sm font-semibold text-white hover:bg-pink-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Start again
              </button>
            )}
          </div>
        </div>
      </section>

      {(warnings.length > 0 || isAdultQuest(quest)) && (
        <section className="rounded-3xl border border-amber-900/40 bg-amber-500/5 p-5">
          <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-amber-300">
            Safety & comfort
          </div>
          <div className="mt-3 space-y-2 text-sm leading-6 text-amber-100/90">
            {warnings.length > 0 ? (
              warnings.map((w, idx) => <p key={idx}>• {w}</p>)
            ) : (
              <p>
                Check consent, pace, comfort, and boundaries before you start. Slow down or stop the moment either person wants that.
              </p>
            )}
          </div>
        </section>
      )}

      {hasStories && (
        <section className="rounded-3xl border border-slate-800 bg-slate-950/70 p-5">
          <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400">
            Linked stories
          </div>
          <h2 className="mt-1 text-lg font-bold text-slate-50">
            Story follow-up
          </h2>

          <div className="mt-4 grid gap-3">
            {quest.stories!.map((s: any, idx: number) => (
              <div
                key={String(s?.id || idx)}
                className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4"
              >
                <div className="text-sm font-bold text-slate-100">
                  {String(s?.title || "Untitled story")}
                </div>
                {!!s?.excerpt && (
                  <div className="mt-2 text-sm leading-6 text-slate-400">
                    {String(s.excerpt)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {!hasStories && soloOnly && (
        <section className="rounded-3xl border border-slate-800 bg-slate-950/70 p-5">
          <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400">
            Next steps
          </div>
          <h2 className="mt-1 text-lg font-bold text-slate-50">
            Make future suggestions fit better
          </h2>
          <p className="mt-2 text-sm leading-6 text-slate-300">
            Solo prompts work best when IntimSync knows what you enjoy, what you want to avoid,
            and whether you want to explore alone, with a partner, or in shared spaces.
          </p>

          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <Link
              to="/settings"
              className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 hover:border-pink-400/40"
            >
              <div className="text-sm font-bold text-slate-100">
                Set interests
              </div>
              <div className="mt-2 text-sm leading-6 text-slate-400">
                Tune topics and discovery preferences.
              </div>
            </Link>

            <Link
              to="/settings"
              className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 hover:border-pink-400/40"
            >
              <div className="text-sm font-bold text-slate-100">
                Likes & dislikes
              </div>
              <div className="mt-2 text-sm leading-6 text-slate-400">
                Help us avoid prompts that do not fit.
              </div>
            </Link>

            <Link
              to="/partners"
              className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 hover:border-pink-400/40"
            >
              <div className="text-sm font-bold text-slate-100">
                Connect or explore
              </div>
              <div className="mt-2 text-sm leading-6 text-slate-400">
                Connect with a partner when you want shared prompts.
              </div>
            </Link>
          </div>
        </section>
      )}

      {!hasStories && partnerQuest && (
        <section className="rounded-3xl border border-slate-800 bg-slate-950/70 p-5">
          <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400">
            Shared follow-up
          </div>
          <h2 className="mt-1 text-lg font-bold text-slate-50">
            Finish together, then reflect
          </h2>
          <p className="mt-2 text-sm leading-6 text-slate-300">
            After a partner quest is completed, this area can show reflection, score, or partner feedback when available.
          </p>
        </section>
      )}
    </div>
  );
}
